import Link from "../../common/components/Link";
import Separator from "../../common/components/Separator";
import Placeholder from "../../common/components/Placeholder";
import Post from "../../common/models/Post";
import User from "../../common/models/User";
import icon from "../../common/helpers/icon";
import CommentPost from "./CommentPost";
import DiscussionList from './DiscussionList';
import UserPage, {IUserPageAttrs} from "./UserPage";
import DiscussionListState from '../states/DiscussionListState';
import app from '../../forum/app';
import Mithril from "mithril";
import LogInModal from "./LogInModal";

interface DashboardItem {
  name: 'posts' | 'replies' | 'favorites';
  route_name: string;
}

/**
 * 用户个人中心
 */
export default class PersonUserPage extends UserPage<IUserPageAttrs, DiscussionListState> {
  // 个人中心展示的模块
  userDashboard: DashboardItem[] = [
    {name: 'posts', route_name: 'user.discussions'},
    {name: 'replies', route_name: 'user.posts'},
    {name: 'favorites', route_name: 'user.likes'},
  ];
  // 显示的条数
  loadLimit: number = 3;
  // 回复帖
  replyPosts: Post[] = [];
  // 点赞帖
  linkPosts: Post[] = [];


  oninit(vnode: Mithril.Vnode<IUserPageAttrs, this>) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }

  show(user: User): void {
    super.show(user);

    this.postRefresh(user);
    this.replyRefresh();
    if (this.isActor()) {
      this.linkRefresh();
    }
  }

  content() {
    const isLogin = !!app.session.user;

    return (
      <div className="PersonUserPage">
        <ul className="PersonUserPage-home">
          {this.userDashboard.map((dashboard) => (
            !this.isActor() && dashboard.name === 'favorites'
              ? ""
              : <li className="PersonUserPage-item">
                  <div className="PersonUserPage-header">
                    <div className="PersonUserPage-title">{dashboard.name}</div>
                    <Link
                      href={isLogin ? app.route(dashboard.route_name, {username: this.user!.slug()}) : ''}
                      className="PersonUserPage-viewMore"
                      onclick={(e) => this.showLoginModal(e)}
                    >
                      <span>{app.translator.trans('core.forum.user.dashboard_view_more')}</span>
                      {icon('fas fa-angle-right')}
                    </Link>
                  </div>
                  <Separator />
                  {this.getContent(dashboard.name)}
                </li>
          ))}
        </ul>
      </div>
    );
  }

  // 未登录弹出登录弹框
  showLoginModal(e) {
    const isLogin = !!app.session.user;
    if (!isLogin) {
      // 阻止默认跳转
      e.preventDefault();
      app.modal.show(LogInModal);
    }
  }

  getContent(name: DashboardItem['name']) {
    const contentMap: Record<DashboardItem['name'], Mithril.Children> = {
      posts: <DiscussionList state={this.state} />,
      replies: this.replyOrLinkContent('replies'),
      favorites: this.replyOrLinkContent('favorites')
    };

    return contentMap[name] || null;
  }

  replyOrLinkContent(name: 'replies' | 'favorites') {
    const list = name === 'replies' ? this.replyPosts : this.linkPosts;

    if (list.length === 0) {
      return (
        <div className="PostsUserPage">
          <Placeholder text={app.translator.trans('core.forum.user.posts_empty_text')} />
        </div>
      );
    }

    return (
      <div className="PostsUserPage">
        <ul className="PostsUserPage-list">
          {list.map((post) => (
            <li>
              <div className="PostsUserPage-discussion">
                {app.translator.trans('core.forum.user.in_discussion_text', {
                  discussion: <Link href={app.route.post(post)}>{post.discussion().title()}</Link>,
                })}
              </div>
              <CommentPost post={post} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  isActor() {
    return app.session.user === this.user!;
  }

  // 发帖
  postRefresh(user: User) {
    this.state = new DiscussionListState({
      sort: 'newest',
      filter: { author: user.username() },
      page: { limit: this.loadLimit }
    });

    this.state.refresh().then(() => {
      if (this.state.pages.length > 0) {
        this.state.pages[this.state.pages.length - 1].hasNext = false;
      }
    });
  }

  // 回复
  replyRefresh() {
    this.replyPosts = [];
    m.redraw();
    this.loadResults('replies').then((reply) => {
      this.replyPosts.push(...reply);
      m.redraw();
    });
  }

  // 点赞
  linkRefresh() {
    this.linkPosts = [];
    m.redraw();
    this.loadResults('favorites').then((links) => {
      this.linkPosts.push(...links);
      m.redraw();
    });
  }

  loadResults(type: 'replies' | 'favorites') {
    const params = {
      sort: '-createdAt',
      filter: {
        type: 'comment',
        ...(type === 'replies'  ? { author: this.user!.username() } : { likedBy: this.user!.id() })
      },
      page: { offset: 0, limit: this.loadLimit }
    };

    return app.store.find<Post[]>('posts', params);
  }
}
